import React, { useEffect, useState } from "react";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  ZoomControl,
  useMap,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import Select from "react-select";
import axios from "axios";
import moment from "moment";
import BaseUrl from "../../utils/BaseUrl";
import ContentSection from "../Content";
import "./Leaflet.css";
import { isDatePassed } from "../../utils/IsDatePassed";

const initialPosition = [5.318531667, -4.019558333];

const mapStyle = {
  height: "85vh",
  width: "100%",
  position: "relative",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  overflow: "hidden",
};

const customMarkerIcon = L.icon({
  iconSize: [25, 35],
  iconAnchor: [12, 41],
  popupAnchor: [2, -40],
  iconUrl: "https://unpkg.com/leaflet@1.6/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png",
});

// 🟡 Icône jaune = En cours
const yellowMarker = L.icon({
  iconSize: [25, 35],
  iconAnchor: [12, 41],
  popupAnchor: [2, -40],
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
  shadowUrl: "https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png",
});

// 🔵 Icône bleu = Validé
const greenMarker = L.icon({
  iconSize: [25, 35],
  iconAnchor: [12, 41],
  popupAnchor: [2, -40],
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png",
});

const redMarker = L.icon({
  iconSize: [25, 35],
  iconAnchor: [12, 41],
  popupAnchor: [2, -40],
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png",
});


/* ✅ Composant séparé pour centrer la carte dynamiquement */
function FlyToMarker({ selected }) {
  const map = useMap();

  useEffect(() => {
    if (selected) {
      map.flyTo([selected.latitude, selected.longitude], 15, {
        animate: true,
        duration: 1.5,
      });
    }
  }, [selected, map]);

  return null;
}

const Leaflet = () => {
  const [loading, setLoading] = useState(false);
  const [itemsList, setItemsList] = useState([]);
  const [selectedPdv, setSelectedPdv] = useState(null);

  const url = BaseUrl();

  // const customMarkerIcon = L.icon({
  //   iconSize: [25, 35],
  //   iconAnchor: [12, 41],
  //   popupAnchor: [2, -40],
  //   iconUrl: "https://unpkg.com/leaflet@1.6/dist/images/marker-icon.png",
  //   shadowUrl: "https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png",
  // });

  useEffect(() => {
    fetchItemsList();
  }, []);

  const fetchItemsList = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${url.base}/asset-geolocalise`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("_token_")}`,
        },
      });
      if (resp.status === 200) {
        setItemsList(resp.data.data);
      }
    } catch (error) {
      console.error("Erreur de chargement :", error);
    } finally {
      setLoading(false);
    }
  };

  const optionpvs = itemsList.map((option) => ({
    label: option.d_pdv,
    value: option.code,
    latitude: option.d_latitude,
    longitude: option.d_longitude,
  }));

  const handleChangePdv = (selectedOption) => {
    if (selectedOption) {
      setSelectedPdv(selectedOption);
    } else {
      setSelectedPdv(null);
    }
  };

  return (
    <ContentSection ulShownav="gest-affectations" navactive="geolocalisation">
      <div className="p-3 bg-light" style={{ borderRadius: "10px" }}>
        <h5 className="fw-bold mb-3 text-center text-primary">
          🗺️ Géolocalisation des Points de Vente
        </h5>

        <div className="mb-3">
          <Select
            options={optionpvs}
            isClearable
            placeholder="🔍 Rechercher un point de vente..."
            onChange={handleChangePdv}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>

        <div style={mapStyle} className="position-relative">
          <MapContainer
            center={initialPosition}
            zoom={8}
            scrollWheelZoom
            style={mapStyle}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
            />

            <ZoomControl position="bottomright" />

            {/* ✅ Composant pour gérer le flyTo dynamique */}
            <FlyToMarker selected={selectedPdv} />

            <MarkerClusterGroup chunkedLoading>
              {itemsList.map((item, idx) => (
                <Marker
                  key={idx}
                  position={[item.d_latitude, item.d_longitude]}
                  icon={
                    item.status === 'En cours'
                      ? yellowMarker        // EN COURS
                      : item.status === 'Validé'
                      ? greenMarker          
                      : item.status === 'Validé' && isDatePassed(item.contrat_date_fin) ? redMarker 
                      : customMarkerIcon   
                  }

                >
                  <Popup>
                    <div className="p-2" style={{ minWidth: "220px", fontSize: "14px" }}>
                      <h6 className="fw-bold text-primary mb-2">
                        {item.d_pdv?.toUpperCase()}
                      </h6>
                      <p>
                        <b>Contact :</b> {item.d_contact}
                        <br />
                        <b>Territoire :</b> {item.d_territoire}
                        <br />
                        {item.status == "Validé" &&
                          <>
                              <br />
                              <b>Asset :</b> {item?.materiel?.materiel?.libelle}
                              <br />
                              <b>Début :</b>{" "}
                              {moment(item.contrat_date_debut).format("DD/MM/YYYY")}
                              <br />
                              <b>Fin :</b>{" "}
                              {moment(item.contrat_date_fin).format("DD/MM/YYYY")}
                              <br />
                              <b>Durée du contrat:</b>{" "}
                              {moment(item.contrat_date_fin).diff(
                                item.contrat_date_debut,
                                "days"
                              )}{" "}
                              jours
                              <br />
                             <b>Fin du contrat dans :</b>{" "}
                              {Math.max(
                                moment(item.contrat_date_fin).diff(moment(), "days"),
                                0
                              )}{" "}
                              jours

                          </>
                        }
                        
                      </p>

                      <div className="text-center mt-2">
                        {item.statut_contrat === 2 && (
                          <span className="badge bg-success">✅ Actif</span>
                        )}
                        {item.statut_contrat === 3 && (
                          <span className="badge bg-danger">❌ Terminé</span>
                        )}
                        {moment(item.contrat_date_fin).isBefore(moment()) && (
                          <span className="badge bg-warning text-dark ms-2">
                            ⚠️ Expiré
                          </span>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>

        {loading && (
          <div className="text-center mt-3">
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}
      </div>
    </ContentSection>
  );
};

export default Leaflet;
